/*
 *
 *  * Copyright (c) [hoarfroster-frontend] 2026. All Rights Reserved.
 *  *
 *  * Last Modified on Jun 21, 2026 by hoarfroster
 *
 */

import httpRequest from '@utils/http-request';

const API_BASE = import.meta.env.VITE_SERVER_ENDPOINT;

export interface SearchResult {
    type: 'recipe' | 'ingredient' | 'tool' | 'cuisine-type';
    id: string;
    name: string;
    description?: string;
}

export class Search {
    static unifiedSearch = async (query: string): Promise<SearchResult[]> => {
        const queries = new URLSearchParams();
        queries.append('query', query);

        const response = await httpRequest(`${API_BASE}/cooking/search?${queries.toString()}`, 'GET'),
            json = await response.json();

        if (response.status !== 200) {
            throw new Error(json.error ?? 'Search failed');
        }

        // The endpoint may return an array, a `{ results: [...] }` envelope, or an
        // object grouping results by type. Tolerate all shapes so a mismatch can
        // never crash with "json.data.map is not a function".
        const data = json.data as unknown;
        const rawItems: unknown[] = Array.isArray(data)
            ? data
            : Array.isArray((data as { results?: unknown[] })?.results)
              ? (data as { results: unknown[] }).results
              : data && typeof data === 'object'
                ? Object.values(data as Record<string, unknown>).flat()
                : [];

        return rawItems
            .filter((item): item is Record<string, unknown> => !!item && typeof item === 'object')
            .map((i) => ({
                type: i.type as SearchResult['type'],
                id: i.id as string,
                name: i.name as string,
                description: i.description as string | undefined,
            }));
    };
}
